
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import regulationService from '../../services/championshipRegulationService';
import championshipService from '../../services/championshipService';
import useAuth from '../../hooks/useAuth';
import { Button, Card, Form, Spinner, Alert, Tab, Tabs } from 'react-bootstrap';
import { FaEdit, FaSave, FaTimes, FaFilePdf, FaUpload, FaDownload } from 'react-icons/fa';
import './ChampionshipRegulations.css';

function ChampionshipRegulations() {
  const { id: championshipId } = useParams();
  const { user } = useAuth();

  const [championship, setChampionship] = useState(null);
  const [regulation, setRegulation] = useState('');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [activeTab, setActiveTab] = useState('texto');
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // 🔹 Busca dados do campeonato para validar se o usuário é criador/gestor
  useEffect(() => {
    if (!championshipId) return;

    const fetchChampionship = async () => {
      try {
        const res = await championshipService.getById(championshipId);
        setChampionship(res.data || res);
      } catch (err) {
        console.error('Erro ao buscar campeonato:', err);
      }
    };

    fetchChampionship();
  }, [championshipId]);

  // 🔹 Verifica se o usuário é criador, gestor do campeonato ou admin
  const isManager = useMemo(() => {
    if (!user || !championship) return false;
    
    // Admin sempre pode gerenciar
    if (user.role && user.role.toLowerCase() === 'admin') return true;
    
    const userId = user._id;
    const isCreator = championship.creator?._id === userId;
    const isManager = championship.managers?.some(m => m._id === userId);
    return isCreator || isManager;
  }, [user, championship]);

  // 🔹 Buscar dados do regulamento (texto)
  useEffect(() => {
    if (!championshipId) {
      setError('ID do campeonato não encontrado na URL');
      setLoading(false);
      return;
    }

    const fetchRegulationData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Buscar texto do regulamento
        const textRes = await regulationService.get(championshipId);
        setRegulation(textRes?.content || '');

      } catch (err) {
        console.error('Erro ao buscar regulamento:', err);
        setError(err.response?.data?.message || 'Falha ao carregar o regulamento');
      } finally {
        setLoading(false);
      }
    };

    fetchRegulationData();
  }, [championshipId]);

  // 🔹 Buscar PDF separadamente (apenas quando necessário) - ATUALIZADA
  const fetchPdfData = async () => {
    if (!championshipId || pdfLoading) return;
    
    try {
      setPdfLoading(true);
      
      // Chamada atualizada para o serviço corrigido
      const response = await regulationService.getPdf(championshipId);
      
      // 🔹 A nova API retorna { success: true, pdfUrl: 'https://...' }
      if (response.success && response.pdfUrl) {
        setPdfUrl(response.pdfUrl);
      } else {
        // PDF não encontrado ou sucesso: false
        setPdfUrl(null);
        // Não mostramos erro aqui pois é normal não ter PDF
      }
    } catch (err) {
      console.warn('Erro ao buscar PDF:', err.message);
      setPdfUrl(null);
    } finally {
      setPdfLoading(false);
    }
  };

  // 🔹 Carregar PDF apenas quando a aba PDF for selecionada
  useEffect(() => {
    if (activeTab === 'pdf' && !pdfUrl && !pdfLoading) {
      fetchPdfData();
    }
  }, [activeTab, pdfUrl, pdfLoading]);

  const handleTextSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await regulationService.save(championshipId, { content: regulation });
      setSuccess('Regulamento salvo com sucesso!');
      setEditing(false);
    } catch (err) {
      console.error('Erro ao salvar regulamento:', err);
      setError(err.response?.data?.message || 'Erro ao salvar o regulamento');
    } finally {
      setSaving(false);
    }
  };

  const handlePdfUpload = async () => {
    if (!pdfFile) return;
    try {
      setUploadingPdf(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('pdf', pdfFile);

      await regulationService.uploadPdf(championshipId, formData);
      
      // Recarregar PDF após upload
      await fetchPdfData();
      
      setSuccess('PDF do regulamento enviado com sucesso!');
      setPdfFile(null); // Limpar seleção de arquivo
      
      // Resetar input de arquivo
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
    } catch (err) {
      console.error('Erro ao enviar PDF:', err);
      setError(err.response?.data?.message || 'Erro ao enviar PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handlePdfDelete = async () => {
    if (!window.confirm('Tem certeza que deseja remover o PDF do regulamento?')) {
      return;
    }
    
    try {
      setUploadingPdf(true);
      await regulationService.deletePdf(championshipId);
      setPdfUrl(null);
      setSuccess('PDF do regulamento removido com sucesso!');
    } catch (err) {
      console.error('Erro ao remover PDF:', err);
      setError(err.response?.data?.message || 'Erro ao remover PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
        <p>Carregando regulamento...</p>
      </div>
    );
  }

  if (!championshipId) {
    return (
      <Alert variant="danger" className="mt-3">
        Erro: Não foi possível identificar o campeonato.
      </Alert>
    );
  }

  return (
    <Card className="regulation-card">
      <Card.Header>
        <h3 className="mb-0">Regulamento do Campeonato</h3>
      </Card.Header>

      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="texto" title="Texto">
            {editing ? (
              <>
                <Form.Group controlId="regulationTextarea" className="mt-3">
                  <Form.Control
                    as="textarea"
                    value={regulation}
                    onChange={(e) => setRegulation(e.target.value)}
                    rows={15}
                    className="regulation-textarea"
                    disabled={saving}
                    placeholder="Digite o regulamento aqui..."
                  />
                </Form.Group>
                <div className="d-flex gap-2 mt-3">
                  <Button 
                    variant="success" 
                    onClick={handleTextSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <Spinner as="span" animation="border" size="sm" />
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Salvar
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setEditing(false)}
                    disabled={saving}
                  >
                    <FaTimes className="me-2" />
                    Cancelar
                  </Button>
                </div>
              </>
            ) : (
              <div className="regulation-content">
                {regulation ? (
                  <pre style={{ 
                    whiteSpace: 'pre-wrap', 
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    fontFamily: 'inherit',
                    fontSize: '1rem',
                    lineHeight: '1.6',
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '0.25rem',
                    border: '1px solid #e9ecef',
                    maxWidth: '100%'
                  }}>
                    {regulation}
                  </pre>
                ) : (
                  <div className="text-muted text-center py-4">
                    Nenhum regulamento textual cadastrado.
                    {isManager && (
                      <div className="mt-2">
                        <Button 
                          variant="outline-primary" 
                          onClick={() => setEditing(true)}
                          size="sm"
                        >
                          <FaEdit className="me-1" />
                          Criar Regulamento
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {regulation && isManager && (
                  <Button 
                    variant="outline-primary" 
                    onClick={() => setEditing(true)}
                    className="mt-3"
                  >
                    <FaEdit className="me-2" />
                    Editar Texto
                  </Button>
                )}
              </div>
            )}
          </Tab>

          <Tab eventKey="pdf" title="PDF">
            <div className="pdf-section">
              {pdfLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Carregando PDF...</p>
                </div>
              ) : pdfUrl ? (
                <>
                  <div className="pdf-viewer-container">
                    <iframe
                      src={pdfUrl}
                      title="Visualizador de PDF"
                      width="100%"
                      height="500px"
                      style={{ border: 'none' }}
                      onError={() => {
                        console.error('Erro ao carregar iframe do PDF');
                        setError('Não foi possível carregar o PDF. Verifique se o arquivo está acessível.');
                      }}
                    />
                    <div className="mt-3 d-flex gap-2 flex-wrap">
                      <Button 
                        variant="primary" 
                        href={pdfUrl} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="me-2 mb-2"
                      >
                        <FaDownload className="me-2" />
                        Abrir em Nova Aba
                      </Button>
                      {isManager && (
                        <Button 
                          variant="danger" 
                          onClick={handlePdfDelete}
                          disabled={uploadingPdf}
                          className="mb-2"
                        >
                          {uploadingPdf ? (
                            <Spinner as="span" animation="border" size="sm" />
                          ) : (
                            'Remover PDF'
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-5">
                  <FaFilePdf className="text-muted mb-3" size={48} />
                  <p className="text-muted">Nenhum PDF cadastrado</p>
                  {isManager && (
                    <p className="text-muted small mt-2">
                      Faça upload de um arquivo PDF abaixo
                    </p>
                  )}
                </div>
              )}

              {isManager && (
                <Form.Group className="mt-4">
                  <Form.Label>
                    <strong>Enviar PDF do Regulamento</strong>
                  </Form.Label>
                  <div className="d-flex gap-2 align-items-start">
                    <Form.Control
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                      disabled={uploadingPdf}
                      className="flex-grow-1"
                    />
                    <Button 
                      variant="success" 
                      onClick={handlePdfUpload}
                      disabled={!pdfFile || uploadingPdf}
                    >
                      {uploadingPdf ? (
                        <Spinner as="span" animation="border" size="sm" />
                      ) : (
                        <>
                          <FaUpload className="me-2" />
                          Enviar
                        </>
                      )}
                    </Button>
                  </div>
                  <Form.Text muted className="d-block mt-1">
                    <small>
                      • Tamanho máximo: 5MB
                      <br />
                      • Apenas arquivos PDF são permitidos
                      <br />
                      • O PDF existente será substituído
                    </small>
                  </Form.Text>
                </Form.Group>
              )}
            </div>
          </Tab>
        </Tabs>
      </Card.Body>
    </Card>
  );
}

export default ChampionshipRegulations;
